/**
 * Mini-Project Team — in-memory store + event-driven dirty model.
 *
 * POLICY_TEAM_ROSTERS.md §3. The Mini-Project team table IS the ACL
 * (role='lead' anchors write authority; role='member' grants read +
 * own-media). Editing happens in the Settings modal's Team tab; the
 * parent modal's Save button persists via persistMiniProjectTeamToApi
 * which hits the bulk-save endpoint.
 *
 * Pattern mirrors src/Data/CommunityConfig/communityEnablers.ts:
 *   - hydrateMiniProjectTeamFromApi(guid) seeds the store on first open.
 *   - getTeamForMiniProject(guid) returns the current local state.
 *   - upsertTeamMember / removeTeamMember / setTeamForMiniProject
 *     mutate the store and fire l4e_miniproject_team_changed for the
 *     Settings tab to re-render.
 *   - persistMiniProjectTeamToApi(guid) does the single bulk PUT and
 *     fires l4e_miniproject_team_persisted for public consumers.
 *
 * The _changed / _persisted split prevents in-flight admin edits from
 * leaking to public surfaces before the admin clicks Save. Same fix
 * pattern that closed the Geo Community settings-to-Alignment-tab leak.
 */

import {
  fetchMiniProjectTeamMembers,
  bulkSaveMiniProjectTeamMembers,
  type MiniProjectTeamMember,
  type BulkMiniProjectTeamMember,
} from "../../api/miniProjects";

/** Local-edit shape. Adds a `_localOnly` flag so the persist step knows
 *  which rows to send with a null teamMemberGuid (new) vs an existing
 *  one (update). _localOnly rows carry a client-generated GUID so React
 *  list keys stay stable across re-renders. */
export interface LocalMiniProjectTeamMember extends MiniProjectTeamMember {
  /** True when this row was added in the current edit session and has
   *  not yet been persisted to the server. */
  _localOnly?: boolean;
}

/* ------------------------------------------------------------------ */
/*  In-memory store                                                    */
/* ------------------------------------------------------------------ */

/** Map<mini_project_guid (UPPER), LocalMiniProjectTeamMember[]> */
const teamStore = new Map<string, LocalMiniProjectTeamMember[]>();

/** Set<mini_project_guid (UPPER)> — which MPs have been hydrated. */
const hydrated = new Set<string>();

function notifyChanged(miniProjectGuid: string): void {
  window.dispatchEvent(
    new CustomEvent("l4e_miniproject_team_changed", {
      detail: { miniProjectGuid },
    }),
  );
}

function notifyPersisted(miniProjectGuid: string): void {
  window.dispatchEvent(
    new CustomEvent("l4e_miniproject_team_persisted", {
      detail: { miniProjectGuid },
    }),
  );
}

/* ------------------------------------------------------------------ */
/*  Public API                                                          */
/* ------------------------------------------------------------------ */

export function getTeamForMiniProject(miniProjectGuid: string): LocalMiniProjectTeamMember[] {
  return teamStore.get(miniProjectGuid.toUpperCase()) || [];
}

export function setTeamForMiniProject(
  miniProjectGuid: string,
  members: LocalMiniProjectTeamMember[],
): void {
  teamStore.set(miniProjectGuid.toUpperCase(), members);
  notifyChanged(miniProjectGuid);
}

export function upsertTeamMember(
  miniProjectGuid: string,
  member: LocalMiniProjectTeamMember,
): void {
  const key = miniProjectGuid.toUpperCase();
  const existing = teamStore.get(key) || [];
  const idx = existing.findIndex(
    (m) => m.teamMemberGuid.toUpperCase() === member.teamMemberGuid.toUpperCase(),
  );
  if (idx >= 0) {
    existing[idx] = { ...member };
  } else {
    existing.push({ ...member });
  }
  teamStore.set(key, existing);
  notifyChanged(miniProjectGuid);
}

export function removeTeamMember(miniProjectGuid: string, teamMemberGuid: string): void {
  const key = miniProjectGuid.toUpperCase();
  const existing = teamStore.get(key) || [];
  const target = teamMemberGuid.toUpperCase();
  teamStore.set(
    key,
    existing.filter((m) => m.teamMemberGuid.toUpperCase() !== target),
  );
  notifyChanged(miniProjectGuid);
}

/** Reset the in-memory store to the server-authoritative state.
 *  Called when the admin cancels editing or after a successful save. */
export function discardLocalEdits(miniProjectGuid: string): void {
  hydrated.delete(miniProjectGuid.toUpperCase());
  teamStore.delete(miniProjectGuid.toUpperCase());
  notifyChanged(miniProjectGuid);
}

/* ------------------------------------------------------------------ */
/*  API hydration                                                       */
/* ------------------------------------------------------------------ */

/**
 * Fetch active team members from the API and seed the store. Safe to
 * call multiple times — skips when already hydrated unless force=true.
 * On API failure, leaves the store as-is so the UI doesn't blank out
 * mid-edit; caller can retry by passing force=true.
 *
 * Hydration race protection (3b bug Skip reported 2026-05-20):
 *   The Team tab fires this from a useEffect on mount. If the user
 *   adds a member or edits a title before the fetch resolves, those
 *   changes land in the local store while the fetch is still in
 *   flight. Without protection, the fetch's response would replace
 *   the entire store and silently wipe the user's in-progress edits
 *   — manifesting as "I picked someone but they didn't appear" and
 *   "I changed the title but it reverted."
 *
 *   Fix: merge instead of replace. Take the server rows as the base
 *   and append any _localOnly rows from the current store. Edits to
 *   existing server rows during the race window are still lost
 *   (rare — would require editing a Lead row's displayTitle in the
 *   sub-second between mount and fetch resolution), but the dominant
 *   cases (add-during-fetch, edit-during-fetch on a freshly-added
 *   row) are preserved.
 */
export async function hydrateMiniProjectTeamFromApi(
  miniProjectGuid: string,
  force = false,
): Promise<void> {
  const key = miniProjectGuid.toUpperCase();
  if (!force && hydrated.has(key)) return;

  try {
    const rows = await fetchMiniProjectTeamMembers(miniProjectGuid, false);
    const serverRows: LocalMiniProjectTeamMember[] = rows.map((r) => ({ ...r }));

    // Race resolution: if local edits arrived between mount and now,
    // preserve any _localOnly additions. Force-rehydrate (force=true)
    // bypasses this — used after a successful save when the server
    // response IS the authoritative truth.
    if (force) {
      teamStore.set(key, serverRows);
    } else {
      const existingLocalOnly = (teamStore.get(key) || []).filter((m) => m._localOnly);
      teamStore.set(key, [...serverRows, ...existingLocalOnly]);
    }

    hydrated.add(key);
    notifyChanged(miniProjectGuid);
  } catch {
    // Leave the store alone. Retryable by callers via force=true.
  }
}

/* ------------------------------------------------------------------ */
/*  Persist                                                              */
/* ------------------------------------------------------------------ */

/** Convert a local row to the bulk-save payload shape. Rows added in
 *  this edit session (`_localOnly=true`) are sent with `teamMemberGuid:
 *  null` so the server inserts a fresh row with its own GUID. */
function toBulkPayload(m: LocalMiniProjectTeamMember): BulkMiniProjectTeamMember {
  return {
    teamMemberGuid: m._localOnly ? null : m.teamMemberGuid,
    userGuid: m.userGuid,
    role: m.role === "lead" ? "lead" : "member",
    displayTitle: (m.displayTitle || "").trim() || (m.role === "lead" ? "Lead" : "Member"),
    sortOrder: m.sortOrder,
    isHidden: m.isHidden,
  };
}

/**
 * Persist the current local state to the bulk-save endpoint. On success,
 * reseeds the store with the server-authoritative response (which
 * includes any server-side mutations like new TeamMemberGuids for
 * inserted rows) and fires l4e_miniproject_team_persisted so public
 * surfaces can refresh.
 *
 * Throws on validation or network errors; the caller (Settings modal
 * Save flow) surfaces the message.
 */
export async function persistMiniProjectTeamToApi(miniProjectGuid: string): Promise<void> {
  const key = miniProjectGuid.toUpperCase();
  const current = teamStore.get(key) || [];
  const payload = current.map(toBulkPayload);

  const response = await bulkSaveMiniProjectTeamMembers(miniProjectGuid, payload);
  teamStore.set(key, response.map((r) => ({ ...r })));
  hydrated.add(key);
  notifyChanged(miniProjectGuid);
  notifyPersisted(miniProjectGuid);
}
